import { useEffect, useMemo, useState } from 'react'
import { REGIE_GROUPING_LABELS, erfuelltRecht, type RegieProposal, geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { inlineInput } from '@/lib/form-styles'
import { acceptRegieProposal, fetchRegieProposal } from '../api/project-sales.api'
import { alsTag } from '../project-labels'
import { bestaetige } from '@/lib/dialoge'

const schmal = inlineInput.replace('w-full', '')
/** Betrag im Geldformat (`geld.ts`); leer → „—". */
const geld = (n: number | null | undefined) => geldText(n) || '—'
const stunden = (n: number) => n.toLocaleString(navigator.language, { maximumFractionDigits: 2 })
const heute = () => new Date().toISOString().slice(0, 10)

const HINWEIS: Record<string, string> = {
  REGIE_QUALIFICATION_UNKNOWN: 'Qualifikation unbekannt',
  REGIE_NO_RATE: 'kein Regiesatz — Satz 0',
}

/**
 * AP-35 Detailblatt 7 3.3 — **Regievorschlag prüfen und übernehmen.**
 *
 * Links die Buchungen je Tag mit Häkchen (abwählen = später vorschlagen), rechts
 * die Zeilen, die daraus entstehen. Die Vorschau rechnet der Server; beim
 * Übernehmen rechnet er neu — was hier steht, ist Anzeige, kein Wert, der
 * mitgeschickt wird.
 */
export function RegieProposalDialog({
  positionId,
  scheme,
  onClose,
  onDone,
}: {
  positionId: string
  scheme: string | null
  onClose: () => void
  onDone: () => Promise<void> | void
}) {
  const { user } = useAuthStore()
  const preise = erfuelltRecht('projects.price.view', user?.permissions)
  const [bis, setBis] = useState(heute())
  const [v, setV] = useState<RegieProposal | null>(null)
  const [aus, setAus] = useState<Set<string>>(new Set())
  const [ohneVerbrauch, setOhneVerbrauch] = useState<Set<string>>(new Set())
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    setV(null)
    fetchRegieProposal(positionId, bis)
      .then((x) => {
        setV(x)
        setAus(new Set())
        setOhneVerbrauch(new Set())
      })
      .catch((e) => setFehler(apiErrorText(e, 'Der Vorschlag ließ sich nicht laden.')))
  }, [positionId, bis])

  const frei = useMemo(() => (v?.entries ?? []).filter((e) => !e.blockedBy), [v])
  const gewaehlt = frei.filter((e) => !aus.has(e.timeEntryId))
  const verbrauch = (v?.consumables ?? []).filter((c) => !ohneVerbrauch.has(c.consumableId))
  // Rechts nur die Gruppen, die noch eine gewählte Buchung haben.
  const gruppen = (v?.groups ?? []).filter((g) => g.entryIds.some((id) => !aus.has(id)))
  const nachTag = useMemo(() => {
    const m = new Map<string, NonNullable<typeof v>['entries']>()
    for (const e of v?.entries ?? []) m.set(e.date, [...(m.get(e.date) ?? []), e])
    return [...m.entries()]
  }, [v])

  async function uebernehmen() {
    if (!v) return
    const warnung = v.schemeWarning
      ? await bestaetige('Diese Position wird nicht nach Regie abgerechnet — Regiestunden sind dort Kosten, kein Anspruch. Trotzdem als Nachtrag übernehmen?')
      : true
    if (!warnung) return
    setLaeuft(true)
    setFehler(null)
    try {
      await acceptRegieProposal({
        positionId,
        until: bis,
        timeEntryIds: gewaehlt.map((e) => e.timeEntryId),
        consumableIds: verbrauch.map((c) => c.consumableId),
        confirmNotTimeAndMaterial: v.schemeWarning || undefined,
      })
      await onDone()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-lg border border-border shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3">
          <h2 className="text-lg font-semibold">Regievorschlag</h2>
          <label className="text-sm text-muted-foreground">
            Stichtag
            <input type="date" className={`${schmal} ml-2 w-40`} value={bis} onChange={(e) => setBis(e.target.value || heute())} />
          </label>
          {v && <span className="text-sm text-muted-foreground">{REGIE_GROUPING_LABELS[v.grouping]} · Namen {v.namesOnInvoice ? 'auf dem Beleg' : 'nur intern'}</span>}
          {(v?.schemeWarning ?? (scheme && scheme !== 'TIME_AND_MATERIAL')) && (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">Position nicht nach Regie abgerechnet</span>
          )}
        </div>

        {/* AP-27 4.2: Regieberichte, die noch auf einem Gerät liegen — ihre Stunden fehlen hier noch. */}
        {v?.reportsOnDevice?.length ? (
          <div className="mx-5 mt-3 rounded-md bg-amber-100 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
            {v.reportsOnDevice.map((r) => (
              <p key={r.number}>
                Regiebericht {r.number} vom {alsTag(r.documentDate)} liegt noch auf dem Gerät von {r.userName} — erst übertragen lassen.
              </p>
            ))}
          </div>
        ) : null}
        {!v && !fehler && <p className="p-5 text-sm text-muted-foreground">Lädt …</p>}
        {v && (
          <div className="grid min-h-0 flex-1 gap-4 overflow-auto p-5 lg:grid-cols-2">
            <div>
              <h3 className="mb-1 text-sm font-medium">Buchungen bis {alsTag(v.until)}</h3>
              {v.entries.length === 0 && <p className="text-sm text-muted-foreground">Keine Buchungen auf dieser Position.</p>}
              <table className="w-full text-sm">
                <tbody>
                  {nachTag.map(([tag, liste]) => [
                    <tr key={tag} className="border-b border-border bg-muted/40 text-xs font-medium">
                      <td colSpan={preise ? 5 : 4} className="px-2 py-1">
                        {alsTag(tag)}
                      </td>
                    </tr>,
                    ...liste.map((e) => (
                      <tr key={e.timeEntryId} className={`border-b border-border ${e.blockedBy ? 'opacity-50' : ''}`}>
                        <td className="w-6 px-2 py-1">
                          <input
                            type="checkbox"
                            disabled={!!e.blockedBy}
                            checked={!e.blockedBy && !aus.has(e.timeEntryId)}
                            aria-label={`${e.userName} ${e.date}`}
                            onChange={() =>
                              setAus((a) => {
                                const n = new Set(a)
                                if (n.has(e.timeEntryId)) n.delete(e.timeEntryId)
                                else n.add(e.timeEntryId)
                                return n
                              })
                            }
                          />
                        </td>
                        <td className="px-2 py-1">{e.userName}</td>
                        <td className="px-2 py-1">
                          {e.qualification ?? '—'}
                          {e.hints.map((h) => (
                            <span key={h} className="block text-xs text-amber-700 dark:text-amber-400">
                              {HINWEIS[h] ?? h}
                            </span>
                          ))}
                          {e.blockedBy && (
                            <span className="block text-xs text-muted-foreground">
                              {e.blockedBy.code === 'REGIE_ENTRY_IN_SITE_REPORT' ? `im Regiebericht ${e.blockedBy.label}` : `abgerechnet über „${e.blockedBy.label}"`}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{stunden(e.hours)} h</td>
                        {preise && <td className="px-2 py-1 text-right tabular-nums">{geld(e.rate)}</td>}
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="mb-1 text-sm font-medium">entstehende Zeilen</h3>
              {gruppen.length === 0 && verbrauch.length === 0 && <p className="text-sm text-muted-foreground">Nichts gewählt.</p>}
              <ul className="space-y-2 text-sm">
                {gruppen.map((g) => (
                  <li key={g.key} className="rounded border border-border p-2">
                    <div className="flex justify-between gap-2 font-medium">
                      <span>{g.name}</span>
                      <span className="tabular-nums">
                        {stunden(g.hours)} h{preise && g.rate != null ? ` × ${geld(g.rate)} = ${geld(g.total)}` : ''}
                      </span>
                    </div>
                    <p className="whitespace-pre-line text-xs text-muted-foreground">{g.longText}</p>
                    {g.hints.map((h) => (
                      <p key={h} className="text-xs text-amber-700 dark:text-amber-400">
                        {HINWEIS[h] ?? h}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
              {v.consumables.length > 0 && (
                <div className="mt-3">
                  <h3 className="mb-1 text-sm font-medium">Verbrauch</h3>
                  <ul className="space-y-1 text-sm">
                    {v.consumables.map((c) => (
                      <li key={c.consumableId} className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={!ohneVerbrauch.has(c.consumableId)}
                          aria-label={c.name}
                          onChange={() =>
                            setOhneVerbrauch((a) => {
                              const n = new Set(a)
                              if (n.has(c.consumableId)) n.delete(c.consumableId)
                              else n.add(c.consumableId)
                              return n
                            })
                          }
                        />
                        <span className="flex-1">
                          {c.quantity.toLocaleString(navigator.language)} {c.unitCode} {c.name}
                          <span className="block text-xs text-muted-foreground">
                            {alsTag(c.date)}
                            {c.needsArticle && ' · ohne Artikel'}
                          </span>
                        </span>
                        {preise && <span className="tabular-nums">{geld(c.total)}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {aus.size > 0 && <p className="mt-2 text-xs text-muted-foreground">Abgewählte Buchungen bleiben frei und werden später wieder vorgeschlagen. Die Summe rechnet der Server beim Übernehmen neu.</p>}
              {preise && v.total != null && aus.size === 0 && ohneVerbrauch.size === 0 && <p className="mt-2 text-right font-medium tabular-nums">Summe {geld(v.total)}</p>}
            </div>
          </div>
        )}

        {fehler && <p className="px-5 pb-2 text-sm text-destructive">{fehler}</p>}
        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
            Schließen
          </button>
          <button
            type="button"
            disabled={laeuft || (gewaehlt.length === 0 && verbrauch.length === 0)}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            onClick={() => void uebernehmen()}
          >
            {verbrauch.length ? `${gewaehlt.length} Buchungen und ${verbrauch.length} Verbrauch übernehmen` : `${gewaehlt.length} Buchungen übernehmen`}
          </button>
        </div>
      </div>
    </div>
  )
}
